import {minutesToHours} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';
import {AttributesRow} from 'platform/components';
import {Heading, HStack, Link, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useSelector} from 'react-redux';

import {always} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {GetFileByIdApiResponse} from '@dms/api/base';
import {useGetTenantQuery} from '@dms/api/core';
import {GetVehicleApiResponseV2} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  ColorCircle,
  formatPowerWithHorsepower,
  UmweltplaketteEnum,
  usePermissions,
  useVehicleCatalogue,
  VehicleCustomFields,
} from '@dms/shared';
import {
  selectBatteryOwnershipType,
  selectBatteryType,
  selectBodyColor,
  selectBodyStyle,
  selectChargingConnectorType,
  selectColorType,
  selectDoorCount,
  selectDrive,
  selectEmissionClass,
  selectFuelType,
  selectHybridType,
  selectInteriorColor,
  selectInteriorMaterial,
  selectTransmission,
  selectVehicleMakeModel,
  FuelTypeEnum,
  HybridTypeEnum,
  VehicleCatalogueEnumOption,
  VehicleCatalogueFuelTypeProperties,
  useGetVehicleEnums,
} from '@dms/teas';

import {parseDate, buildArray, EMPTY_PLACEHOLDER, openFile} from 'shared/web';

import {MAKE_OTHER, MODEL_FAMILY_OTHER_END} from '../constants/makes';
import {getRealProductionDate} from '../utils/getRealProductionDate';
import {getSecondaryFuelType} from '../utils/getSecondaryFuelType';

// Pure function to determine consumption unit based on fuel type
const getConsumptionUnitForFuelType = (
  fuelType: FuelTypeEnum | undefined,
  i18n: {t: (key: string) => string}
) =>
  match(fuelType)
    .with(FuelTypeEnum.FUELTYPE_ELECTRIC, () => i18n.t('general.metric.kwh100km'))
    .with(FuelTypeEnum.FUELTYPE_HYDROGEN, FuelTypeEnum.FUELTYPE_ETHANOL, () =>
      i18n.t('general.metric.kg100km')
    )
    .otherwise(() => i18n.t('general.metric.l100km'));

/**
 *
 * @param vehicle
 * @param certificateFile - Optional battery certificate file metadata
 * @returns set of vehicle parameters, to display in vehicle detail set by
 * https://www.figma.com/file/uyvxXnfc6w7UXwVA3tHISj/Business-case-2.0?node-id=7605%3A265021&t=smZD0cmw84l649K9-3
 *
 * @example
 * const [
 getModelSpecifications,
 engineSpecifications,
 consumptionAndEmissionSpecifications,
 metricsSpecifications
 ] = useVehicleDetailsParameters(vehicle, certificateFile)
 *
 */
export const useVehicleDetailsParameters = (
  vehicle: GetVehicleApiResponseV2,
  certificateFile?: GetFileByIdApiResponse
) => {
  const formatDateTime = useDateTimeFormatter();
  const [vehicleUtils] = useVehicleCatalogue(vehicle.type);
  const {data: tenantData} = useGetTenantQuery();
  const isGermanTenant = tenantData?.country === 'DEU';
  const isWltpCo2Visible = tenantData?.country === 'DEU' || tenantData?.country === 'BEL';

  const [canReadDocuments] = usePermissions({
    permissionKeys: ['readDocuments'],
  });

  const vehicleType = vehicleUtils.getVehicleType(vehicle.type);
  const transmission = useSelector(selectTransmission(vehicle.transmission));
  const doorCountCategory = useSelector(selectDoorCount(vehicle.doorCountCategory));
  const driveType = useSelector(selectDrive(vehicle.drive));
  const exteriorColor = useSelector(selectBodyColor(vehicle?.exteriorColor?.code, vehicle?.type));
  const colorType = useSelector(
    selectColorType(vehicle?.exteriorColorSpecification, vehicle?.type)
  );
  const interiorColor = useSelector(
    selectInteriorColor(vehicle?.interiorColor?.code, vehicle?.type)
  );
  const interiorMaterial = useSelector(
    selectInteriorMaterial(vehicle?.interiorMaterial, vehicle?.type)
  );
  const interiorColorCode = interiorColor?.properties?.find(
    (property) => property.name === 'color_code'
  )?.value;
  const exteriorColorCode = exteriorColor?.properties?.find(
    (property) => property.name === 'color_code'
  )?.value;
  const hybridType = useSelector(selectHybridType(vehicle?.hybridType, vehicle?.type));
  const emissionClass = useSelector(selectEmissionClass(vehicle.emissionClass));
  const bodyStyle = useSelector(selectBodyStyle(vehicle.bodyStyle, vehicle?.type));
  const makeModel = useSelector(selectVehicleMakeModel(vehicle.make));
  const fuelType = useSelector(selectFuelType(vehicle.fuelType, vehicle?.type));
  const BatteryOwnershipType = useSelector(
    selectBatteryOwnershipType(vehicle?.batteryOwnershipType, vehicle.type)
  );
  const batteryType = useSelector(selectBatteryType(vehicle?.batteryType, vehicle.type));
  const chargingConnectorType = useSelector(
    selectChargingConnectorType(vehicle?.chargingConnectorType, vehicle.type)
  );
  const secondaryFuelType = useSelector(
    selectFuelType(
      getSecondaryFuelType(
        fuelType as VehicleCatalogueEnumOption<VehicleCatalogueFuelTypeProperties>,
        vehicle.secondaryFuelType as string
      ),
      vehicle.type
    )
  );
  const {dimensions_height, dimensions_length} = useGetVehicleEnums(vehicle.type);

  const model = makeModel?.models.find((model) => model.value === vehicle.modelFamily);

  const hsn = vehicle?.customFields?.find(
    (field) => field.definitionId === VehicleCustomFields.hsn.definitionId
  );
  const tsn = vehicle?.customFields?.find(
    (field) => field.definitionId === VehicleCustomFields.tsn.definitionId
  );
  const umweltplakette = vehicle?.customFields?.find(
    (field) => field.definitionId === VehicleCustomFields.umweltplakette.definitionId
  );

  const umweltplaketteLabelMapper: Record<UmweltplaketteEnum, string> = {
    [UmweltplaketteEnum.none]: i18n.t('entity.vehicle.labels.umweltplakette.none'),
    [UmweltplaketteEnum.red]: i18n.t('entity.vehicle.labels.umweltplakette.red'),
    [UmweltplaketteEnum.yellow]: i18n.t('entity.vehicle.labels.umweltplakette.yellow'),
    [UmweltplaketteEnum.green]: i18n.t('entity.vehicle.labels.umweltplakette.green'),
  };

  const firstRegistrationOn = isNotNil(vehicle.firstRegistrationOn)
    ? parseDate(vehicle.firstRegistrationOn ?? '')
    : null;

  const vehicleReservationDateString = firstRegistrationOn
    ? formatDateTime('dateShort', firstRegistrationOn)
    : null;

  const isSecondaryFuelLPGorCNG = match(fuelType?.value)
    .with(FuelTypeEnum.FUELTYPE_LPG, FuelTypeEnum.FUELTYPE_CNG, always(true))
    .otherwise(always(false));

  const isSecondaryFuelLPGorCNGorHydrogen = match(fuelType?.value)
    .with(
      FuelTypeEnum.FUELTYPE_LPG,
      FuelTypeEnum.FUELTYPE_CNG,
      FuelTypeEnum.FUELTYPE_HYDROGEN,
      always(true)
    )
    .otherwise(always(false));

  const isHybrid = match(fuelType?.value)
    .with(FuelTypeEnum.FUELTYPE_HYBRID, always(true))
    .otherwise(always(false));

  const isElectric = fuelType?.value === FuelTypeEnum.FUELTYPE_ELECTRIC;

  const isFullyHybrid =
    fuelType?.value === FuelTypeEnum.FUELTYPE_HYBRID &&
    hybridType?.value === HybridTypeEnum.HYBRIDTYPE_FULL;

  const isPHEVHybrid =
    fuelType?.value === FuelTypeEnum.FUELTYPE_HYBRID &&
    hybridType?.value === HybridTypeEnum.HYBRIDTYPE_PHEV;

  const isNotElectricNorHydrogen =
    fuelType?.value !== FuelTypeEnum.FUELTYPE_ELECTRIC &&
    fuelType?.value !== FuelTypeEnum.FUELTYPE_HYDROGEN;

  const isElectricOrHybrid =
    fuelType?.value === FuelTypeEnum.FUELTYPE_ELECTRIC ||
    fuelType?.value === FuelTypeEnum.FUELTYPE_HYBRID;

  const isSecondaryFuelPetrolOrDiesel =
    fuelType?.value &&
    (vehicle.secondaryFuelType === FuelTypeEnum.FUELTYPE_DIESEL ||
      vehicle.secondaryFuelType === FuelTypeEnum.FUELTYPE_PETROL);
  const isPrimaryFuelPetrolOrDiesel =
    fuelType?.value === FuelTypeEnum.FUELTYPE_DIESEL ||
    fuelType?.value === FuelTypeEnum.FUELTYPE_PETROL;

  const isVanOrTruck = vehicle.type === 'VEHICLETYPE_VAN' || vehicle.type === 'VEHICLETYPE_TRUCK';

  const isTrailerOrSemiTrailer =
    vehicle.type === 'VEHICLETYPE_TRAILER' || vehicle.type === 'VEHICLETYPE_SEMI_TRAILER';

  const shouldShowEuropalletStorageSpaces =
    vehicle.type === 'VEHICLETYPE_TRAILER' ||
    vehicle.type === 'VEHICLETYPE_SEMI_TRAILER' ||
    vehicle.type === 'VEHICLETYPE_TRUCK';

  const shouldShowCargoDimensions = isVanOrTruck || isTrailerOrSemiTrailer;

  const canHaveParticularFilter =
    (isPrimaryFuelPetrolOrDiesel || isSecondaryFuelPetrolOrDiesel) && isNotNil(vehicle.hasDpf);

  const getModelSpecifications = buildArray<AttributesRow>()
    .add({
      label: i18n.t('entity.vehicle.labels.licensePlate'),
      value: vehicle?.registrationPlate,
      testId: 'licensePlate',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.vin'),
      value: vehicle.vin,
      testId: 'vin',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.type'),
      value: vehicleType,
      testId: 'type',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.make'),
      value:
        vehicle.make === MAKE_OTHER ? (vehicle.customMake ?? makeModel?.label) : makeModel?.label,
      testId: 'make',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.model'),
      value: vehicle.modelFamily?.includes(MODEL_FAMILY_OTHER_END)
        ? vehicle?.customModelFamily || model?.label || vehicle?.modelFamily
        : model?.label || vehicle?.modelFamily,
      testId: 'model',
    })
    .whenNot(isTrailerOrSemiTrailer, {
      label: i18n.t('entity.vehicle.labels.trim'),
      value: vehicle.trim,
      testId: 'trim',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.variantName'),
      value: vehicle?.variant,
      testId: 'variantName',
    })
    .when(isGermanTenant, {
      label: `${i18n.t('entity.vehicle.labels.hsn')} / ${i18n.t('entity.vehicle.labels.tsn')}`,
      value: `${hsn?.value?.value ?? EMPTY_PLACEHOLDER} / ${tsn?.value?.value ?? EMPTY_PLACEHOLDER}`,
    })
    .add({
      label: i18n.t('entity.vehicle.labels.firstRegistration'),
      value: vehicleReservationDateString,
      testId: 'firstRegistration',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.realProduction'),
      value: getRealProductionDate(vehicle.manufacturedOn),
      testId: 'realProduction',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.modelYear'),
      value: vehicle.modelYear,
      testId: 'modelYear',
    })
    .when(isTrailerOrSemiTrailer, {
      label: i18n.t('entity.vehicle.labels.axleCount'),
      value: vehicle.axleCount,
      testId: 'axleCount',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.body'),
      value: bodyStyle?.label,
      testId: 'body',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.fuel'),
      value: fuelType?.label,
      testId: 'fuel',
    })
    .when(isNotNilOrEmpty(secondaryFuelType), {
      label: i18n.t('entity.vehicle.labels.secondaryFuelType'),
      value: secondaryFuelType?.label,
      testId: 'secondaryFuelType',
    })
    .when(isSecondaryFuelLPGorCNG, {
      label: i18n.t('entity.vehicle.labels.gasRevisionValidUntil'),
      value: vehicle?.gasRevisionValidUntil
        ? formatDateTime('dateShort', parseDate(vehicle?.gasRevisionValidUntil))
        : null,
      testId: 'gasRevisionValidUntil',
    })
    .when(isSecondaryFuelLPGorCNGorHydrogen, {
      label: i18n.t('entity.vehicle.labels.pressureVesselExpires'),
      value: vehicle?.pressureVesselValidUntil
        ? formatDateTime('dateShort', parseDate(vehicle?.pressureVesselValidUntil))
        : null,
      testId: 'pressureVesselExpires',
    })
    .when(isHybrid, {
      label: i18n.t('general.labels.hybridType'),
      value: hybridType?.label,
      testId: 'hybridType',
    })
    .when(isElectric, {
      label: `${i18n.t('entity.vehicle.labels.batteryChargingAc')} (${i18n.t(
        'general.metric.kW'
      )})`,
      value: vehicle?.electricVehicleBattery?.chargingAC,
      testId: 'batteryChargingAc',
    })
    .when(isElectric, {
      label: `${i18n.t('entity.vehicle.labels.batteryChargingTimeAc')} (${i18n.t(
        'general.metric.h'
      )})`,
      value: vehicle?.chargingTimeAC?.toString(),
      testId: 'batteryChargingTimeAc',
    })
    .when(isElectric, {
      label: `${i18n.t('entity.vehicle.labels.batteryChargingDc')} (${i18n.t(
        'general.metric.kW'
      )})`,
      value: vehicle?.electricVehicleBattery?.chargingDC,
      testId: 'batteryChargingDc',
    })
    .when(isElectric, {
      label: `${i18n.t('entity.vehicle.labels.batteryChargingTimeDc')} (${i18n.t(
        'general.metric.min'
      )})`,
      value: vehicle?.chargingTimeDC?.toString(),
      testId: 'batteryChargingTimeDc',
    })
    .when(isElectric, {
      label: i18n.t('entity.vehicle.labels.batteryType'),
      value: batteryType?.label,
      testId: 'batteryType',
    })
    .when(isElectric, {
      label: `${i18n.t('entity.vehicle.labels.batteryCapacity')} (${i18n.t('general.metric.kWh')})`,
      value: vehicle?.electricVehicleBattery?.batteryCapacityWh,
      testId: 'batteryCapacityWh',
    })
    .when(isElectric, {
      label: `${i18n.t('entity.vehicle.labels.batteryCapacity')} (${i18n.t('general.metric.Ah')})`,
      value: vehicle?.electricVehicleBattery?.batteryCapacityAh,
      testId: 'batteryCapacityAh',
    })
    .when(isElectric, {
      label: `${i18n.t('entity.vehicle.labels.batteryVoltage')} (${i18n.t('general.metric.kW')})`,
      value: vehicle?.electricVehicleBattery?.batteryVoltage,
      testId: 'batteryVoltage',
    })
    .when(isElectric, {
      label: i18n.t('entity.vehicle.labels.batteryCount'),
      value: vehicle?.batteryCount?.toString(),
      testId: 'batteryCount',
    })
    .when(isElectric, {
      label: i18n.t('entity.vehicle.labels.batteryChargingConnectoryType'),
      value: chargingConnectorType?.label,
      testId: 'batteryChargingConnectoryType',
    })
    .when(isElectric, {
      label: `${i18n.t('entity.vehicle.labels.batteryElectricRange')} (${i18n.t(
        'general.metric.km'
      )})`,
      value: vehicle?.electricRange?.toString(),
      testId: 'batteryElectricRange',
    })
    .when(isElectricOrHybrid, {
      label: `${i18n.t('entity.vehicle.labels.electricRangeCity')} (${i18n.t(
        'general.metric.km'
      )})`,
      value: vehicle?.electricRangeCity?.toString(),
      testId: 'electricRangeCity',
    })
    .when(isElectric, {
      label: `${i18n.t('entity.vehicle.labels.batteryMileageLimit')} (${i18n.t(
        'general.metric.km'
      )})`,
      value: vehicle?.batteryMileageLimit?.toString(),
      testId: 'batteryMileageLimit',
    })
    .when(isElectric, {
      label: i18n.t('entity.vehicle.labels.batteryWarrantyUntil'),
      value: vehicle?.batteryWarrantyUntil
        ? formatDateTime('dateShort', parseDate(vehicle?.batteryWarrantyUntil))
        : null,
      testId: 'batteryWarrantyUntil',
    })
    .when(isElectric, {
      label: i18n.t('entity.vehicle.labels.batteryOwnershipType'),
      value: BatteryOwnershipType?.label,
      testId: 'batteryOwnershipType',
    })
    .when(isElectricOrHybrid, {
      label: i18n.t('entity.vehicle.labels.biDirectionalCharging'),
      value: vehicle?.biDirectionalCharging ?? null,
      testId: 'biDirectionalCharging',
    })
    .when(isElectricOrHybrid, {
      label: i18n.t('entity.vehicle.labels.batteryCondition'),
      value: isNotNil(vehicle?.batteryCondition)
        ? `${vehicle.batteryCondition}%`
        : EMPTY_PLACEHOLDER,
      testId: 'batteryCondition',
    })
    .when(isElectricOrHybrid, {
      label: i18n.t('entity.vehicle.labels.batteryConditionMeasuredAt'),
      value: isNotNil(vehicle?.batteryConditionMeasuredAt)
        ? formatDateTime('dateShort', parseDate(vehicle?.batteryConditionMeasuredAt))
        : null,
      testId: 'batteryConditionMeasuredAt',
    })
    .when(isElectricOrHybrid, {
      label: `${i18n.t('entity.vehicle.labels.actualBatteryRange')} (${i18n.t(
        'general.metric.km'
      )})`,
      value: vehicle?.actualBatteryRange?.toString(),
      testId: 'actualBatteryRange',
    })
    .when(isElectricOrHybrid, {
      label: i18n.t('entity.vehicle.labels.batteryCertificate'),
      value: match(vehicle?.batteryCertificate)
        .with(true, () => i18n.t('general.labels.yes'))
        .with(false, () => i18n.t('general.labels.no'))
        .otherwise(() => EMPTY_PLACEHOLDER),
      testId: 'batteryCertificate',
    })
    .when(
      isElectricOrHybrid &&
        isNotNil(vehicle.certificateFileId) &&
        isNotNil(certificateFile) &&
        canReadDocuments,
      {
        label: '',
        content: certificateFile ? (
          <Link onClick={() => openFile(certificateFile.originalUri)} size="small">
            {certificateFile.originalFilename}
          </Link>
        ) : null,
        testId: 'batteryCertificateDocument',
      }
    )
    .when(isElectric, {
      label: i18n.t('entity.vehicle.labels.hasRangeExtender'),
      value: vehicle?.hasRangeExtender ? i18n.t('general.labels.yes') : i18n.t('general.labels.no'),
      testId: 'hasRangeExtender',
    })
    .when(isFullyHybrid, {
      label: i18n.t('entity.vehicle.labels.batteryType'),
      value: batteryType?.label,
      testId: 'batteryType',
    })
    .when(isFullyHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryCapacity')} (${i18n.t('general.metric.kWh')})`,
      value: vehicle?.electricVehicleBattery?.batteryCapacityWh,
      testId: 'batteryCapacityWh',
    })
    .when(isFullyHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryCapacity')} (${i18n.t('general.metric.Ah')})`,
      value: vehicle?.electricVehicleBattery?.batteryCapacityAh,
      testId: 'batteryCapacityAh',
    })
    .when(isFullyHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryVoltage')} (${i18n.t('general.metric.kW')})`,
      value: vehicle?.electricVehicleBattery?.batteryVoltage,
      testId: 'batteryVoltage',
    })
    .when(isFullyHybrid, {
      label: i18n.t('entity.vehicle.labels.batteryCount'),
      value: vehicle?.batteryCount?.toString(),
      testId: 'batteryCount',
    })
    .when(isFullyHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryElectricRange')} (${i18n.t(
        'general.metric.km'
      )})`,
      value: vehicle?.electricRange?.toString(),
      testId: 'batteryElectricRange',
    })
    .when(isHybrid, {
      label: `${i18n.t('entity.vehicle.labels.combinedRange')} (${i18n.t('general.metric.km')})`,
      value: vehicle?.combinedRange?.toString(),
      testId: 'combinedRange',
    })
    .when(isHybrid, {
      label: `${i18n.t('entity.vehicle.labels.combinedRangeCity')} (${i18n.t(
        'general.metric.km'
      )})`,
      value: vehicle?.combinedRangeCity?.toString(),
      testId: 'combinedRangeCity',
    })
    .when(isFullyHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryMileageLimit')} (${i18n.t(
        'general.metric.km'
      )})`,
      value: vehicle?.batteryMileageLimit?.toString(),
      testId: 'batteryMileageLimit',
    })
    .when(isFullyHybrid, {
      label: i18n.t('entity.vehicle.labels.batteryWarrantyUntil'),
      value: isNotNil(vehicle?.batteryWarrantyUntil)
        ? formatDateTime('dateShort', parseDate(vehicle?.batteryWarrantyUntil ?? 0))
        : null,
      testId: 'batteryWarrantyUntil',
    })
    .when(isPHEVHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryChargingAc')} (${i18n.t(
        'general.metric.kW'
      )})`,
      value: vehicle?.electricVehicleBattery?.chargingAC,
      testId: 'batteryChargingAc',
    })
    .when(isPHEVHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryChargingTimeAc')} (${i18n.t(
        'general.metric.h'
      )})`,
      value: isNotNil(vehicle?.chargingTimeAC)
        ? (convertHoursToTime(vehicle?.chargingTimeAC ?? 0) ?? null)
        : null,
      testId: 'batteryChargingTimeAc',
    })
    .when(isPHEVHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryChargingDc')} (${i18n.t(
        'general.metric.kW'
      )})`,
      value: vehicle?.electricVehicleBattery?.chargingDC,
      testId: 'batteryChargingDc',
    })
    .when(isPHEVHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryChargingTimeDc')} (${i18n.t(
        'general.metric.min'
      )})`,
      value: isNotNil(vehicle?.chargingTimeDC)
        ? (convertMinutesToTime(vehicle?.chargingTimeDC ?? 0) ?? null)
        : null,
      testId: 'batteryChargingTimeDc',
    })
    .when(isPHEVHybrid, {
      label: i18n.t('entity.vehicle.labels.batteryType'),
      value: batteryType?.label,
      testId: 'batteryType',
    })
    .when(isPHEVHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryCapacity')} (${i18n.t('general.metric.kWh')})`,
      value: vehicle?.electricVehicleBattery?.batteryCapacityWh,
      testId: 'batteryCapacityWh',
    })
    .when(isPHEVHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryCapacity')} (${i18n.t('general.metric.Ah')})`,
      value: vehicle?.electricVehicleBattery?.batteryCapacityAh,
      testId: 'batteryCapacityAh',
    })
    .when(isPHEVHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryVoltage')} (${i18n.t('general.metric.kW')})`,
      value: vehicle?.electricVehicleBattery?.batteryVoltage,
      testId: 'batteryVoltage',
    })
    .when(isPHEVHybrid, {
      label: i18n.t('entity.vehicle.labels.batteryCount'),
      value: vehicle?.batteryCount?.toString(),
      testId: 'batteryCount',
    })
    .when(isPHEVHybrid, {
      label: i18n.t('entity.vehicle.labels.batteryChargingConnectoryType'),
      value: chargingConnectorType?.label,
      testId: 'batteryChargingConnectoryType',
    })
    .when(isPHEVHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryElectricRange')} (${i18n.t(
        'general.metric.km'
      )})`,
      value: vehicle?.electricRange?.toString(),
      testId: 'batteryElectricRange',
    })
    .when(isPHEVHybrid, {
      label: `${i18n.t('entity.vehicle.labels.batteryMileageLimit')} (${i18n.t(
        'general.metric.km'
      )})`,
      value: vehicle?.batteryMileageLimit?.toString(),
      testId: 'batteryMileageLimit',
    })
    .when(isPHEVHybrid, {
      label: i18n.t('entity.vehicle.labels.batteryWarrantyUntil'),
      value: isNotNil(vehicle?.batteryWarrantyUntil)
        ? formatDateTime('dateShort', parseDate(vehicle?.batteryWarrantyUntil ?? 0))
        : null,
      testId: 'batteryWarrantyUntil',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.transmission'),
      value: transmission?.label,
      testId: 'transmission',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.drive'),
      value: driveType?.label,
      testId: 'drive',
    })
    .add({
      label: i18n.t('entity.sourcing.vehiclePreview.labels.power'),
      value: isNotNil(vehicle.power) ? formatPowerWithHorsepower(vehicle.power) : null,
      testId: 'power',
    })
    .when(isNotNil(vehicle.maximalPower) && isFeatureEnabled(featureFlags.CORE_MAX_POWER), {
      label: i18n.t('entity.vehicle.labels.maximalPower'),
      value: formatPowerWithHorsepower(Number(vehicle.maximalPower)),
      testId: 'maximalPower',
    })
    .when(isHybrid, {
      label: i18n.t('entity.vehicle.labels.powerCombustionEngine'),
      value: isNotNil(vehicle.powerCombustionEngine)
        ? formatPowerWithHorsepower(Number(vehicle.powerCombustionEngine))
        : null,
      testId: 'powerCombustionEngine',
    })
    .when(isHybrid, {
      label: i18n.t('entity.vehicle.labels.powerElectricEngine'),
      value: isNotNil(vehicle.powerElectricEngine)
        ? formatPowerWithHorsepower(Number(vehicle.powerElectricEngine))
        : null,
      testId: 'powerElectricEngine',
    })
    .when(isNotElectricNorHydrogen, {
      label: `${i18n.t('entity.vehicle.labels.engineCapacity')} (${i18n.t('general.metric.ccm')})`,
      value: isNotNil(vehicle.engineVolume) ? vehicle.engineVolume.toString() : null,
      testId: 'engineCapacity',
    })
    .when(isNotElectricNorHydrogen, {
      label: i18n.t('entity.vehicle.labels.numberOfCylinders'),
      value: isNotNil(vehicle.cylinderCount) ? vehicle.cylinderCount.toString() : null,
      testId: 'numberOfCylinders',
    })
    .when(fuelType?.value, {
      label: i18n.t('entity.vehicle.labels.numberOfGears'),
      value: isNotNil(vehicle.gearCount) ? vehicle.gearCount.toString() : null,
      testId: 'numberOfGears',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.numberOfDoors'),
      value: doorCountCategory?.label,
      testId: 'numberOfDoors',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.numberOfSeats'),
      value: vehicle.seatCount?.toString(),
      testId: 'numberOfSeats',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.bodyColorType'),
      value: colorType?.label,
      testId: 'bodyColorType',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.bodyColor'),
      content: exteriorColor ? (
        <HStack align="center" spacing={1}>
          <ColorCircle
            colorLabel={exteriorColor?.label}
            colorCode={exteriorColorCode}
            needBorder={exteriorColor?.value === 'COLOR_WHITE'}
            isDisabled
          />
          <Text size="xSmall">{exteriorColor?.label}</Text>
        </HStack>
      ) : null,
      testId: 'bodyColor',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.manufacturerColorName'),
      value: vehicle?.manufacturerColorName,
      testId: 'manufacturerColorName',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.manufacturerColorCode'),
      value: vehicle?.manufacturerColorCode,
      testId: 'manufacturerColorCode',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.interiorMaterial'),
      value: interiorMaterial?.label,
      testId: 'interiorMaterial',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.interiorColor'),
      content: interiorColor ? (
        <HStack align="center" spacing={1}>
          <ColorCircle
            colorLabel={interiorColor?.label}
            colorCode={interiorColorCode}
            needBorder={interiorColor?.value === 'COLOR_WHITE'}
          />
          <Text size="xSmall">{interiorColor?.label}</Text>
        </HStack>
      ) : null,
      testId: 'interiorColor',
    })
    .add({
      label: i18n.t('entity.vehicle.labels.interiorColorName'),
      value: vehicle?.interiorColorName,
      testId: 'interiorColorName',
    });

  const engineSpecifications = buildArray<AttributesRow>([
    {
      label: i18n.t('entity.vehicle.labels.engineName'),
      value: vehicle.engineName || null,
      testId: 'engineName',
    },
    {
      label: i18n.t('entity.vehicle.labels.engineNumber'),
      value: vehicle.engineNumber || null,
      testId: 'engineNumber',
    },
    {
      label: i18n.t('entity.vehicle.labels.engineCode'),
      value: vehicle.engineCode || null,
      testId: 'engineCode',
    },
    {
      label: i18n.t('entity.vehicle.labels.transmissionNumber'),
      value: vehicle.transmissionNumber || null,
      testId: 'transmissionNumber',
    },
    {
      label: i18n.t('entity.vehicle.labels.transmissionCode'),
      value: vehicle.transmissionCode || null,
      testId: 'transmissionCode',
    },
  ]).when(isNotNil(fuelType?.value), {
    label: i18n.t('entity.vehicle.labels.maxTorque'),
    value: vehicle?.maxTorque || null,
    testId: 'maxTorque',
  });

  // Determine consumption unit based on fuel type
  const consumptionUnit = getConsumptionUnitForFuelType(
    fuelType?.value as FuelTypeEnum | undefined,
    i18n
  );

  // For PHEV, fuel consumption should always use l/100km (regardless of what consumptionUnit returns)
  const wltpLiquidUnit = isPHEVHybrid ? i18n.t('general.metric.l100km') : consumptionUnit;

  // Check if vehicle has any WLTP consumption data
  const hasAnyWltpLiquidData =
    isNotNil(vehicle.fuelConsumptionWltpCombined) ||
    isNotNil(vehicle.fuelConsumptionWltpCityCenter) ||
    isNotNil(vehicle.fuelConsumptionWltpOutskirts) ||
    isNotNil(vehicle.fuelConsumptionWltpCountryRoads) ||
    isNotNil(vehicle.fuelConsumptionWltpHighway) ||
    isNotNil(vehicle.fuelConsumptionWltpWeighted);

  const hasAnyWltpElectricData =
    isNotNil(vehicle.fuelConsumptionWltpCombinedKwh) ||
    isNotNil(vehicle.fuelConsumptionWltpCityCenterKwh) ||
    isNotNil(vehicle.fuelConsumptionWltpOutskirtsKwh) ||
    isNotNil(vehicle.fuelConsumptionWltpCountryRoadsKwh) ||
    isNotNil(vehicle.fuelConsumptionWltpHighwayKwh) ||
    isNotNil(vehicle.fuelConsumptionWltpWeightedCombinedKwh);

  // Check if vehicle has any NEDC consumption data
  const hasAnyNedcData =
    isNotNil(vehicle.fuelConsumptionCombined) ||
    isNotNil(vehicle.fuelConsumptionUrban) ||
    isNotNil(vehicle.fuelConsumptionExtraUrban);

  const consumptionAndEmissionSpecifications = buildArray<AttributesRow>()
    // WLTP Section (liquid/gas consumption) - hide for pure electric vehicles
    .when(!isElectric && hasAnyWltpLiquidData, {
      label: '',
      content: (
        <Heading size={5}>
          {isPHEVHybrid
            ? i18n.t('entity.vehicle.labels.fuelConsumptionDischargedBatterySectionTitle')
            : i18n.t('entity.vehicle.labels.consumptionStandardWltp')}
        </Heading>
      ),
      testId: 'wltpSectionHeader',
    })
    .when(!isElectric && isNotNil(vehicle.fuelConsumptionWltpCombined), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCombined'),
      value: isNotNil(vehicle.fuelConsumptionWltpCombined)
        ? `${vehicle.fuelConsumptionWltpCombined.toString()} ${wltpLiquidUnit}`
        : null,
      testId: 'fuelConsumptionWltpCombined',
    })
    .when(!isElectric && isNotNil(vehicle.fuelConsumptionWltpCityCenter), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCityCenter'),
      value: isNotNil(vehicle.fuelConsumptionWltpCityCenter)
        ? `${vehicle.fuelConsumptionWltpCityCenter.toString()} ${wltpLiquidUnit}`
        : null,
      testId: 'fuelConsumptionWltpCityCenter',
    })
    .when(!isElectric && isNotNil(vehicle.fuelConsumptionWltpOutskirts), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpOutskirts'),
      value: isNotNil(vehicle.fuelConsumptionWltpOutskirts)
        ? `${vehicle.fuelConsumptionWltpOutskirts.toString()} ${wltpLiquidUnit}`
        : null,
      testId: 'fuelConsumptionWltpOutskirts',
    })
    .when(!isElectric && isNotNil(vehicle.fuelConsumptionWltpCountryRoads), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCountryRoads'),
      value: isNotNil(vehicle.fuelConsumptionWltpCountryRoads)
        ? `${vehicle.fuelConsumptionWltpCountryRoads.toString()} ${wltpLiquidUnit}`
        : null,
      testId: 'fuelConsumptionWltpCountryRoads',
    })
    .when(!isElectric && isNotNil(vehicle.fuelConsumptionWltpHighway), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpHighway'),
      value: isNotNil(vehicle.fuelConsumptionWltpHighway)
        ? `${vehicle.fuelConsumptionWltpHighway.toString()} ${wltpLiquidUnit}`
        : null,
      testId: 'fuelConsumptionWltpHighway',
    })
    .when(isPHEVHybrid && isNotNil(vehicle.fuelConsumptionWltpWeighted), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpWeighted'),
      value: isNotNil(vehicle.fuelConsumptionWltpWeighted)
        ? `${vehicle.fuelConsumptionWltpWeighted.toString()} ${wltpLiquidUnit}`
        : null,
      testId: 'fuelConsumptionWltpWeighted',
    })
    // WLTP Electric consumption - only show header for hybrid vehicles (pure electric doesn't need a separate section header)
    .when(hasAnyWltpElectricData && isHybrid, {
      label: '',
      content: (
        <Heading size={5}>{i18n.t('entity.vehicle.labels.energyConsumptionSectionTitle')}</Heading>
      ),
      testId: 'wltpElectricSectionHeader',
    })
    .when(isElectricOrHybrid && isNotNil(vehicle.fuelConsumptionWltpCombinedKwh), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCombined'),
      value: isNotNil(vehicle.fuelConsumptionWltpCombinedKwh)
        ? `${vehicle.fuelConsumptionWltpCombinedKwh.toString()} ${i18n.t('general.metric.kwh100km')}`
        : null,
      testId: 'fuelConsumptionWltpCombinedKwh',
    })
    .when(isElectricOrHybrid && isNotNil(vehicle.fuelConsumptionWltpCityCenterKwh), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCityCenter'),
      value: isNotNil(vehicle.fuelConsumptionWltpCityCenterKwh)
        ? `${vehicle.fuelConsumptionWltpCityCenterKwh.toString()} ${i18n.t('general.metric.kwh100km')}`
        : null,
      testId: 'fuelConsumptionWltpCityCenterKwh',
    })
    .when(isElectricOrHybrid && isNotNil(vehicle.fuelConsumptionWltpOutskirtsKwh), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpOutskirts'),
      value: isNotNil(vehicle.fuelConsumptionWltpOutskirtsKwh)
        ? `${vehicle.fuelConsumptionWltpOutskirtsKwh.toString()} ${i18n.t('general.metric.kwh100km')}`
        : null,
      testId: 'fuelConsumptionWltpOutskirtsKwh',
    })
    .when(isElectricOrHybrid && isNotNil(vehicle.fuelConsumptionWltpCountryRoadsKwh), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCountryRoads'),
      value: isNotNil(vehicle.fuelConsumptionWltpCountryRoadsKwh)
        ? `${vehicle.fuelConsumptionWltpCountryRoadsKwh.toString()} ${i18n.t('general.metric.kwh100km')}`
        : null,
      testId: 'fuelConsumptionWltpCountryRoadsKwh',
    })
    .when(isElectricOrHybrid && isNotNil(vehicle.fuelConsumptionWltpHighwayKwh), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpHighway'),
      value: isNotNil(vehicle.fuelConsumptionWltpHighwayKwh)
        ? `${vehicle.fuelConsumptionWltpHighwayKwh.toString()} ${i18n.t('general.metric.kwh100km')}`
        : null,
      testId: 'fuelConsumptionWltpHighwayKwh',
    })
    .when(isPHEVHybrid && isNotNil(vehicle.fuelConsumptionWltpWeightedCombinedKwh), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionWltpWeightedCombinedKwh'),
      value: isNotNil(vehicle.fuelConsumptionWltpWeightedCombinedKwh)
        ? `${vehicle.fuelConsumptionWltpWeightedCombinedKwh.toString()} ${i18n.t('general.metric.kwh100km')}`
        : null,
      testId: 'fuelConsumptionWltpWeightedCombinedKwh',
    })
    // NEDC Section
    .when(hasAnyNedcData, {
      label: '',
      content: (
        <Heading size={5}>{i18n.t('entity.vehicle.labels.consumptionStandardNedc')}</Heading>
      ),
      testId: 'nedcSectionHeader',
    })
    .when(isNotNil(vehicle.fuelConsumptionCombined), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionCombined'),
      value: isNotNil(vehicle.fuelConsumptionCombined)
        ? `${vehicle.fuelConsumptionCombined.toString()} ${consumptionUnit}`
        : null,
      testId: 'fuelConsumptionCombined',
    })
    .when(isNotNil(vehicle.fuelConsumptionUrban), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionUrban'),
      value: isNotNil(vehicle.fuelConsumptionUrban)
        ? `${vehicle.fuelConsumptionUrban.toString()} ${consumptionUnit}`
        : null,
      testId: 'fuelConsumptionUrban',
    })
    .when(isNotNil(vehicle.fuelConsumptionExtraUrban), {
      label: i18n.t('entity.vehicle.labels.fuelConsumptionExtraUrban'),
      value: isNotNil(vehicle.fuelConsumptionExtraUrban)
        ? `${vehicle.fuelConsumptionExtraUrban.toString()} ${consumptionUnit}`
        : null,
      testId: 'fuelConsumptionExtraUrban',
    })
    .when(hybridType?.value === HybridTypeEnum.HYBRIDTYPE_FULL, {
      label: i18n.t('entity.vehicle.labels.powerConsumptionCombined'),
      value: isNotNil(vehicle.powerConsumptionCombined)
        ? `${vehicle.powerConsumptionCombined.toString()} ${i18n.t('general.metric.kwh100km')}`
        : null,
      testId: 'powerConsumptionCombined',
    })
    .when(isNotElectricNorHydrogen, {
      label: i18n.t('entity.vehicle.labels.emissionClass'),
      value: emissionClass?.label,
      testId: 'emissionClass',
    })
    .when(isNotElectricNorHydrogen && !isPHEVHybrid, {
      label: i18n.t('entity.vehicle.labels.emissionsCo2WO'),
      value: isNotNil(vehicle.carbonDioxideEmission)
        ? `${vehicle.carbonDioxideEmission.toString()} ${i18n.t('general.metric.gkm')}`
        : null,
      testId: 'emissionsCo2WO',
    })
    .when(
      isNotElectricNorHydrogen &&
        !isPHEVHybrid &&
        isWltpCo2Visible &&
        isNotNil(vehicle.carbonDioxideEmissionClass),
      {
        label: i18n.t('entity.vehicle.labels.carbonDioxideEmissionClass'),
        value: vehicle.carbonDioxideEmissionClass,
        testId: 'carbonDioxideEmissionClass',
      }
    )
    .when(
      isPHEVHybrid && isWltpCo2Visible && isNotNil(vehicle.carbonDioxideEmissionWeightedCombined),
      {
        label: i18n.t('entity.vehicle.labels.carbonDioxideEmissionWeightedCombined'),
        value: isNotNil(vehicle.carbonDioxideEmissionWeightedCombined)
          ? `${vehicle.carbonDioxideEmissionWeightedCombined.toString()} ${i18n.t('general.metric.gkm')}`
          : null,
        testId: 'carbonDioxideEmissionWeightedCombined',
      }
    )
    .when(
      isPHEVHybrid &&
        isWltpCo2Visible &&
        isNotNil(vehicle.carbonDioxideEmissionClassWeightedCombined),
      {
        label: i18n.t('entity.vehicle.labels.carbonDioxideEmissionClassWeightedCombined'),
        value: vehicle.carbonDioxideEmissionClassWeightedCombined ?? null,
        testId: 'carbonDioxideEmissionClassWeightedCombined',
      }
    )
    .when(isPHEVHybrid && isWltpCo2Visible && isNotNil(vehicle.carbonDioxideEmissionDischarged), {
      label: i18n.t('entity.vehicle.labels.carbonDioxideEmissionDischarged'),
      value: isNotNil(vehicle.carbonDioxideEmissionDischarged)
        ? `${vehicle.carbonDioxideEmissionDischarged.toString()} ${i18n.t('general.metric.gkm')}`
        : null,
      testId: 'carbonDioxideEmissionDischarged',
    })
    .when(
      isPHEVHybrid && isWltpCo2Visible && isNotNil(vehicle.carbonDioxideEmissionClassDischarged),
      {
        label: i18n.t('entity.vehicle.labels.carbonDioxideEmissionClassDischarged'),
        value: vehicle.carbonDioxideEmissionClassDischarged ?? null,
        testId: 'carbonDioxideEmissionClassDischarged',
      }
    )
    .when(isGermanTenant, {
      label: i18n.t('entity.vehicle.labels.umweltplakette.title'),
      value: `${umweltplakette?.value?.value ? umweltplaketteLabelMapper[umweltplakette.value.value as UmweltplaketteEnum] : EMPTY_PLACEHOLDER}`,
    })
    .when(canHaveParticularFilter, {
      label: i18n.t('entity.vehicle.labels.dieselParticulateFilter'),
      value: vehicle.hasDpf ? i18n.t('general.labels.yes') : i18n.t('general.labels.no'),
      testId: 'dieselParticulateFilter',
    });

  const metricsSpecifications = buildArray<AttributesRow>([
    {
      label: i18n.t('general.labels.length'),
      value: isNotNilOrEmpty(vehicle.dimensions?.length)
        ? `${vehicle.dimensions?.length} ${i18n.t('general.metric.mm')}`
        : null,
      testId: 'length',
    },
    {
      label: i18n.t('general.labels.width'),
      value: isNotNilOrEmpty(vehicle.dimensions?.width)
        ? `${vehicle.dimensions?.width} ${i18n.t('general.metric.mm')}`
        : null,
      testId: 'width',
    },
    {
      label: i18n.t('general.labels.height'),
      value: isNotNilOrEmpty(vehicle.dimensions?.height)
        ? `${vehicle.dimensions?.height} ${i18n.t('general.metric.mm')}`
        : null,
      testId: 'height',
    },
    {
      label: i18n.t('entity.vehicle.labels.operatingWeight'),
      value: isNotNilOrEmpty(vehicle?.operatingWeight)
        ? `${vehicle?.operatingWeight} ${i18n.t('general.metric.kg')}`
        : null,
      testId: 'operatingWeight',
    },
    {
      label: i18n.t('general.labels.maxWeight'),
      value: isNotNilOrEmpty(vehicle?.weight)
        ? `${vehicle?.weight} ${i18n.t('general.metric.kg')}`
        : null,
      testId: 'maxWeight',
    },
    {
      label: i18n.t('entity.vehicle.labels.maxLoadCapacity'),
      value: isNotNilOrEmpty(vehicle?.maximalLoadCapacity)
        ? `${vehicle?.maximalLoadCapacity} ${i18n.t('general.metric.kg')}`
        : null,
      testId: 'maxLoadCapacity',
    },
    {
      label: i18n.t('entity.vehicle.labels.wheelBase'),
      value: isNotNilOrEmpty(vehicle?.wheelBase)
        ? `${vehicle?.wheelBase} ${i18n.t('general.metric.mm')}`
        : null,
      testId: 'wheelBase',
    },
  ])
    .when(shouldShowEuropalletStorageSpaces, {
      label: i18n.t('entity.vehicle.labels.europalletStorageSpaces'),
      value: isNotNilOrEmpty(vehicle.europalletStorageSpaces)
        ? vehicle.europalletStorageSpaces
        : null,
      testId: 'europalletStorageSpaces',
    })
    .whenNot(isTrailerOrSemiTrailer, {
      label: i18n.t('entity.vehicle.labels.trunkSpace'),
      value: isNotNilOrEmpty(vehicle?.trunkSpace)
        ? `${vehicle?.trunkSpace} ${i18n.t('general.metric.l')}`
        : null,
      testId: 'trunkSpace',
    })
    .when(isVanOrTruck, {
      label: i18n.t('entity.vehicle.labels.vehicleLength'),
      value: dimensions_length?.[vehicle?.lengthCategory ?? '']?.label,
      testId: 'vehicleLength',
    })
    .when(isVanOrTruck, {
      label: i18n.t('entity.vehicle.labels.vehicleHeight'),
      value: dimensions_height?.[vehicle?.heightCategory ?? '']?.label,
      testId: 'vehicleHeight',
    })
    .when(shouldShowCargoDimensions, {
      label: i18n.t('entity.vehicle.labels.loadSpaceLength'),
      value: isNotNilOrEmpty(vehicle?.dimensionsLoadingSpace?.length)
        ? `${vehicle?.dimensionsLoadingSpace?.length} ${i18n.t('general.metric.mm')}`
        : null,
      testId: 'loadSpaceLength',
    })
    .when(shouldShowCargoDimensions, {
      label: i18n.t('entity.vehicle.labels.loadSpaceWidth'),
      value: isNotNilOrEmpty(vehicle?.dimensionsLoadingSpace?.width)
        ? `${vehicle?.dimensionsLoadingSpace?.width} ${i18n.t('general.metric.mm')}`
        : null,
      testId: 'loadSpaceWidth',
    })
    .when(shouldShowCargoDimensions, {
      label: i18n.t('entity.vehicle.labels.loadSpaceHeight'),
      value: isNotNilOrEmpty(vehicle?.dimensionsLoadingSpace?.height)
        ? `${vehicle?.dimensionsLoadingSpace?.height} ${i18n.t('general.metric.mm')}`
        : null,
      testId: 'loadSpaceHeight',
    })
    .when(shouldShowCargoDimensions, {
      label: i18n.t('entity.vehicle.labels.loadArea'),
      value: isNotNilOrEmpty(vehicle?.loadingArea)
        ? `${vehicle?.loadingArea} ${i18n.t('general.metric.m3')}`
        : null,
      testId: 'loadArea',
    });

  return [
    getModelSpecifications,
    engineSpecifications,
    consumptionAndEmissionSpecifications,
    metricsSpecifications,
  ] as const;
};

const convertMinutesToTime = (minutes: number): string | null => {
  if (minutes < 0) {
    return null;
  }

  const timeHours = minutesToHours(minutes).toString().padStart(2, '0');
  const timeMinutes = (minutes % 60).toString().padStart(2, '0');

  return `${timeHours}:${timeMinutes}`;
};

const convertHoursToTime = (hours: number): string | null => {
  if (hours < 0) {
    return null;
  }

  const timeHours = Math.trunc(hours).toString().padStart(2, '0');
  const timeMinutes = Math.trunc((hours * 60) % 60)
    .toString()
    .padStart(2, '0');

  return `${timeHours}:${timeMinutes}`;
};
